import {
  Enrollment as EnrollmentClient,
  Enrollment,
} from '@clear-denver/tpc-openapi-typescript'
import { getXCLEARCorrelationInfo } from '@api/utils'
import { preEnrollmentClient, Client, preEnrollmentConfig } from './config'
import { copyAndRemoveEmptyEntries } from './utils'

export const enrollmentHelperClient = new Enrollment.EnrollmentHelperApi(
  preEnrollmentConfig
)

export async function startEnrollment(
  plan: string,
  client: Client,
  affiliate?: string
): Promise<EnrollmentClient.Enrollment> {
  return client
    .startEnrollment({
      xCLEARCorrelationInfo: getXCLEARCorrelationInfo(),
      planSelectionCommand: {
        plan,
        affiliate,
      },
    })
    .then(({ enrollment }: EnrollmentClient.EnrollmentCreated) => {
      return enrollment
    })
}

export function submitPreEligibility(
  preEligibilityStep: Exclude<
    EnrollmentClient.Enrollment['eligibility'],
    undefined
  >,
  client: Client,
  enrollmentId: string
): Promise<EnrollmentClient.Enrollment> {
  return client.confirmEligibility({
    xCLEARCorrelationInfo: getXCLEARCorrelationInfo(),
    enrollmentId,
    preEligibilityStep,
  })
}

export function submitPreCitizenship(
  preCitizenshipStep: Exclude<
    EnrollmentClient.Enrollment['citizenship'],
    undefined
  >,
  client: Client,
  enrollmentId: string
): Promise<EnrollmentClient.Enrollment> {
  return client.reportCitizenship({
    xCLEARCorrelationInfo: getXCLEARCorrelationInfo(),
    enrollmentId,
    preCitizenshipStep: copyAndRemoveEmptyEntries(preCitizenshipStep),
  })
}

export function submitPreDocumentCheck(
  preDocumentCheckStep: Exclude<
    EnrollmentClient.Enrollment['documentCheck'],
    undefined
  >,
  client: Client,
  enrollmentId: string
): Promise<EnrollmentClient.Enrollment> {
  return client.chooseDocuments({
    xCLEARCorrelationInfo: getXCLEARCorrelationInfo(),
    enrollmentId,
    preDocumentCheckStep: copyAndRemoveEmptyEntries(preDocumentCheckStep),
  })
}

export async function submitPreIdentity(
  preIdentityStep: Exclude<
    EnrollmentClient.Enrollment['identityHints'],
    undefined
  >,
  client: Client,
  enrollmentId: string
): Promise<EnrollmentClient.Enrollment> {
  return client.giveIdentityHints({
    xCLEARCorrelationInfo: getXCLEARCorrelationInfo(),
    enrollmentId,
    preIdentityStep,
  })
}

export async function submitPreBiographicInfo(
  preBiographicInfo: Exclude<
    EnrollmentClient.Enrollment['biographicInfo'],
    undefined
  >,
  client: Client,
  enrollmentId: string
): Promise<EnrollmentClient.Enrollment> {
  return client.biographicInfo({
    xCLEARCorrelationInfo: getXCLEARCorrelationInfo(),
    enrollmentId,
    preBiographicInfoStep: copyAndRemoveEmptyEntries({
      ...preBiographicInfo,
      dateOfBirth: new Date(preBiographicInfo?.dateOfBirth),
    }),
  })
}

export async function submitContact(
  preContactStep: Exclude<EnrollmentClient.Enrollment['contact'], undefined>,
  client: Client,
  enrollmentId: string
): Promise<EnrollmentClient.Enrollment> {
  return client.provideContact({
    xCLEARCorrelationInfo: getXCLEARCorrelationInfo(),
    enrollmentId,
    preContactStep,
  })
}

export async function submitPreAttributes(
  preAttributesStep: Exclude<
    EnrollmentClient.Enrollment['attributes'],
    undefined
  >,
  client: Client,
  enrollmentId: string
): Promise<EnrollmentClient.Enrollment> {
  return client.provideAttributes({
    xCLEARCorrelationInfo: getXCLEARCorrelationInfo(),
    enrollmentId,
    preAttributesStep: {
      ...preAttributesStep,
      aliases: preAttributesStep.aliases.map((item: any) =>
        copyAndRemoveEmptyEntries(item)
      ),
      previousNames: preAttributesStep.previousNames.map((item: any) =>
        copyAndRemoveEmptyEntries(item)
      ),
    },
  })
}

export async function submitPreLegalQuestionnaire(
  preLegalQuestionnaireStep: Exclude<
    EnrollmentClient.Enrollment['legal'],
    undefined
  >,
  client: Client,
  enrollmentId: string
): Promise<EnrollmentClient.Enrollment> {
  return client.answerLegalQuestionnaire({
    xCLEARCorrelationInfo: getXCLEARCorrelationInfo(),
    enrollmentId,
    preLegalQuestionnaireStep,
  })
}

export async function submitFinalizeEnrollment(
  preFinalizeStepCommand: Exclude<
    EnrollmentClient.PreFinalizeStepCommand,
    undefined
  >,
  client: Client,
  enrollmentId: string
): Promise<EnrollmentClient.Enrollment> {
  return client.finalizePreEnrollment({
    xCLEARCorrelationInfo: getXCLEARCorrelationInfo(),
    enrollmentId,
    preFinalizeStepCommand,
  })
}

export async function promotionOpt(
  opt: Enrollment.ClearPromotionsOptOptEnum,
  enrollmentId: string,
  email: string
) {
  return enrollmentHelperClient.clearPromotionsOpt({
    enrollmentId,
    xCLEARCorrelationInfo: getXCLEARCorrelationInfo(),
    opt,
    clearPromotionForm: { email: email },
  })
}

export async function verifyAddress(address: Enrollment.Address) {
  return enrollmentHelperClient.addressSuggestions({
    address,
    xCLEARCorrelationInfo: getXCLEARCorrelationInfo(),
  })
}

export async function uploadDocument(
  enrollmentId: string,
  file: File,
  isNameLinking: boolean,
  type: string
) {
  if (!isNameLinking) {
    return preEnrollmentClient.attachmentUpload({
      xCLEARCorrelationInfo: getXCLEARCorrelationInfo(),
      enrollmentId,
      file,
      fileName: file.name,
      type,
      purposes: [Enrollment.AttachmentUploadPurposesEnum.CITIZENSHIP],
    })
  } else {
    return preEnrollmentClient.attachmentUpload({
      xCLEARCorrelationInfo: getXCLEARCorrelationInfo(),
      enrollmentId,
      file,
      fileName: file.name,
      purposes: [Enrollment.AttachmentUploadPurposesEnum.NAMELINKING],
    })
  }
}

export async function deleteDocument(
  enrollmentId: string,
  attachmentId: string
) {
  return preEnrollmentClient.deleteAttachment({
    xCLEARCorrelationInfo: getXCLEARCorrelationInfo(),
    enrollmentId,
    attachmentId,
  })
}

export async function finishUploadingDocuments(
  preDocumentUploadStepCommand: Exclude<
    EnrollmentClient.Enrollment['documents'],
    undefined
  >,
  client: Client,
  enrollmentId: string
): Promise<EnrollmentClient.Enrollment> {
  return client.finishDocumentUpload({
    xCLEARCorrelationInfo: getXCLEARCorrelationInfo(),
    enrollmentId,
    preDocumentUploadStepCommand,
  })
}

export async function searchExistenceStatusByBiographics(
  biographics: Enrollment.BiographicsLookup
): Promise<Enrollment.ExistenceStatus> {
  return enrollmentHelperClient.searchExistenceByBiographics({
    xCLEARCorrelationInfo: getXCLEARCorrelationInfo(),
    biographicsLookup: biographics,
  })
}

export async function acceptTermsAndConditions(
  accepted: boolean,
  enrollmentId: string
): Promise<void> {
  return preEnrollmentClient.acceptAgreement({
    enrollmentId,
    agreement: {
      agreementType: Enrollment.AgreementAgreementTypeEnum.TERMSANDCONDITIONS,
      accepted,
    },
    xCLEARCorrelationInfo: getXCLEARCorrelationInfo(),
  })
}

export async function finalizePreEnrollment(
  preFinalizeStepCommand: Enrollment.PreFinalizeStepCommand,
  enrollmentId: string
): Promise<EnrollmentClient.Enrollment> {
  return preEnrollmentClient.finalizePreEnrollment({
    xCLEARCorrelationInfo: getXCLEARCorrelationInfo(),
    enrollmentId,
    preFinalizeStepCommand,
  })
}
